import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { apiClient, AssistantDialogueEnty } from 'lib/apiClient'
import { QueryKey } from 'lib/queryClient'

import { usePlaygroundUsers } from './usePlaygroundUsers'

export interface AssistantPlaygroundDialoguesHook {
  data: AssistantDialogueEnty[]
  totalDialogues: number
  isLoading: boolean
  isError: boolean
}

export function useAssistantPlaygroundDialogues(
  enabled: boolean = true,
): AssistantPlaygroundDialoguesHook {
  const {
    findUserById,
    isError: isPlaygroundUsersError,
    isLoading: isPlaygroundUsersLoading,
  } = usePlaygroundUsers()

  const { data, isLoading, isError } = useQuery({
    queryKey: [QueryKey.assistantDialoguesList],
    queryFn: apiClient.getAssistantDialoguesList,
    enabled,
  })

  const parsedData: AssistantDialogueEnty[] = useMemo(
    () =>
      data?.assistantDialogues.map(entry => {
        const parsedEntry: AssistantDialogueEnty = {
          correlationId: entry.correlationId,
          lastInteraction: entry.lastInteraction,
          messageCount: entry.messageCount,
          owner: findUserById(entry.ownerId),
        }
        return parsedEntry
      }) ?? [],
    [data, findUserById],
  )

  return useMemo(
    () => ({
      data: parsedData,
      // No pagination or other tabs are planned, therefore the length of the data represents the total amount
      // of data available
      totalDialogues: parsedData.length,
      isLoading: isLoading || isPlaygroundUsersLoading,
      isError: isError || isPlaygroundUsersError,
    }),
    [isError, isLoading, isPlaygroundUsersError, isPlaygroundUsersLoading, parsedData],
  )
}
